import { Box, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, Flex, Spinner } from '@chakra-ui/react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import fetchProductHighlightList from 'Domain/brief-live/fetchProductHighlightList';

import TableComponent from 'UiKit/table-component';
import SearchComponent from 'UiKit/search';
import ButtonOutline from 'UiKit/button/buttonOutline';
import { TextRegular } from 'UiKit/typography';
import tableList from './const';

import ButtonSolid from 'UiKit/button/buttonSolid';
import { Condition } from 'Component/others';
import SelectProductModalSkeleton from '../skeleton/modal/select-product-modal';

import styles from './styles.module.css';

/**
 * Modal for searching and selecting products to add to a talking point & giveaway.
 *
 * @param {boolean}  isOpen            - Controls modal visibility.
 * @param {function} updateState       - Updates parent state (e.g. isOpenModalSelectProduct, isOpenImagePreview).
 * @param {object}   filterState       - Active filter values (brand, search, page, etc.) passed to the fetch.
 * @param {function} updateFilterState - Updates filterState (e.g. on search input change).
 * @param {function} onConfirm         - Called with selected products array on confirm. Each item: { id, productName, image, skuCode, ... }.
 * @param {Array}    existingProducts  - Already-picked products from the form. Filtered out from the list. Each item must have a `value` field (sku).
 *
 * @example
 * <SelectProductModal
 *   isOpen={isOpenModalSelectProduct}
 *   updateState={updateState}
 *   filterState={filterState}
 *   updateFilterState={updateFilterState}
 *   onConfirm={(items) => handleSelectProductConfirm(items)}
 *   existingProducts={formField?.productHighlight}
 * />
 */
const SelectProductModal = ({ isOpen, updateState, filterState, updateFilterState, onConfirm, existingProducts }) => {
  const { t } = useTranslation('desktop-brief-live');
  const queryClient = useQueryClient();

  const [allProducts, setAllProducts] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState([]);

  const filterStateRef = useRef(filterState);
  const isFetchingRef = useRef(false);
  const pageRef = useRef(1);
  const totalPageRef = useRef(1);

  filterStateRef.current = filterState;

  const doFetch = useCallback(
    async (pageNum) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      setIsFetching(true);
      try {
        const result = await fetchProductHighlightList({
          queryClient,
          params: { ...filterStateRef.current, page: pageNum },
        });
        const resolvedTotalPage = result?.totalPage ?? 1;
        totalPageRef.current = resolvedTotalPage;
        setHasMore(pageNum < resolvedTotalPage);
        setAllProducts((prev) => (pageNum === 1 ? (result?.data ?? []) : [...prev, ...(result?.data ?? [])]));
      } finally {
        isFetchingRef.current = false;
        setIsFetching(false);
      }
    },
    [queryClient],
  );

  const handleScrollReachBottom = useCallback(() => {
    if (isFetchingRef.current || pageRef.current >= totalPageRef.current) return;
    const nextPage = pageRef.current + 1;
    pageRef.current = nextPage;
    doFetch(nextPage);
  }, [doFetch]);

  const existingSkus = new Set((existingProducts || []).map((p) => p?.value).filter(Boolean));
  const filteredProducts = allProducts.filter((p) => !existingSkus.has(p.skuCode));
  const allIds = filteredProducts.map((p) => p.id);
  const isAllSelected = allIds.length > 0 && allIds.every((id) => selectedProduct.includes(id));
  const isPartiallySelected = selectedProduct.length > 0 && !isAllSelected;

  const handleToggle = (id) => setSelectedProduct((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  const handleToggleAll = () => setSelectedProduct(isAllSelected ? [] : allIds);
  const onOpenPreview = (images) => updateState({ isOpenImagePreview: true, selectedImagePreview: images });

  const handleCloseModal = () => {
    setSelectedProduct([]);
    updateState({ isOpenModalSelectProduct: false });
  };

  const handleConfirm = () => {
    onConfirm?.(filteredProducts.filter((p) => selectedProduct.includes(p.id)));
    handleCloseModal();
  };

  const handleSearch = (search) => {
    updateFilterState({ search });
  };

  useEffect(() => {
    if (!isOpen) return;
    pageRef.current = 1;
    setAllProducts([]);
    doFetch(1);
  }, [isOpen, filterState]); // eslint-disable-line

  return (
    <Modal isOpen={isOpen} onClose={handleCloseModal} isCentered>
      <ModalOverlay />
      <ModalContent minWidth={'983px'} maxHeight={'660px'}>
        <ModalHeader>
          <Box>{t('selectProductModal.title')}</Box>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box className={styles['select-product-modal__container']}>
            <Box className={styles['filter__section']}>
              <SearchComponent width="100%" onSearch={handleSearch} value={filterState.search} />
            </Box>
            <Box className={styles['table__section']} position="relative">
              <Condition statement={!(isFetching && allProducts.length === 0)} fallback={<SelectProductModalSkeleton />}>
                <TableComponent
                  columns={tableList({
                    onOpenPreview,
                    selectedIds: selectedProduct,
                    onToggle: handleToggle,
                    isAllSelected,
                    isPartiallySelected,
                    onToggleAll: handleToggleAll,
                    t,
                  })}
                  dataSource={filteredProducts}
                  isDataExist={filteredProducts.length > 0}
                  rowHeight="fit-content"
                  paddingBodyBox="20px 12px"
                  maxTableBodyHeight="350px"
                  verticalAlignBody="top"
                  onScrollReachBottom={handleScrollReachBottom}
                  containerClassName={hasMore ? styles['table__has-more'] : ''}
                  emptyDescription="Tidak ada produk yang ditemukan"
                />
                <Condition statement={isFetching && allProducts.length > 0}>
                  <Box position="absolute" bottom="0" left="0" right="0" display="flex" justifyContent="center" padding="8px" pointerEvents="none">
                    <Spinner size="sm" color="var(--color-primary)" />
                  </Box>
                </Condition>
              </Condition>
            </Box>
            <Box className={styles['footer__section']}>
              <ButtonOutline onClick={handleCloseModal}>{t('common:cancel')}</ButtonOutline>
              <Flex gap="24px" alignItems="center">
                <TextRegular>
                  {selectedProduct.length} {t('selectProductModal.selected')}
                </TextRegular>
                <ButtonSolid onClick={handleConfirm}>{t('common:confirm')}</ButtonSolid>
              </Flex>
            </Box>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SelectProductModal;
